import './ChatResponse.scss';
import React from 'react';
import DOMPurify from 'dompurify';
import { MonitorType } from '../../../../../../../shared/types/monitor/MonitorType';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { getMonitorTypeIcon } from '../../helpers/StatsHelper';
import { getMonitorFullName } from '../../../../../../../shared/helpers/MonitorTypeHelpers';
import { v4 as uuidv4 } from 'uuid';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';

interface ChatResponseProps {
  prompt: string;
  currentPosition: number;
  created: Date;
  content: string;
  organicContent: string;
  htmlContent: string;
  monitorType: MonitorType;
  keyword: string;
  positions: { item: string; position: number }[];
}

const ChatResponse: React.FC<ChatResponseProps> = ({
  prompt,
  currentPosition,
  created,
  content,
  organicContent,
  htmlContent,
  monitorType,
  keyword,
  positions,
}) => {
  const { t } = useTranslation();

  const parser = new DOMParser();

  const htmlSupportedModels = [
    'GEMINI_SEARCH_BROWSER',
    'PERPLEXITY_SEARCH_BROWSER',
    'GROK_SEARCH_BROWSER',
    'COPILOT_SEARCH_BROWSER',
    'GPT_SEARCH_BROWSER',
    'AI_MODE',
  ];

  const isAPIModel = [
    'GPT',
    'GPT_API',
    'GPT_API_BROWSER',
    'GPT_API_SEARCH_WEB',
    'GEMINI_API',
    'GEMINI_API_BROWSER',
    'CLAUDE_API',
    'DEEP_SEEK_API',
    'PERPLEXITY_API',
    'AI_OVERVIEW_NEW',
  ].includes(monitorType);

  const getMainSelector = (monitorType: MonitorType) => {
    switch (monitorType) {
      case 'GEMINI_SEARCH_BROWSER':
        return 'message-content';

      case 'PERPLEXITY_SEARCH_BROWSER':
        return 'div#markdown-content-0';

      case 'COPILOT_SEARCH_BROWSER':
        return 'div[data-content]';

      case 'GROK_SEARCH_BROWSER':
        return '#last-reply-container';

      case 'GPT_SEARCH_BROWSER':
        return 'div[data-message-author-role="assistant"]';

      case 'AI_MODE':
        return 'div[data-container-id="main-col"]';

      default:
        return '';
    }
  };

  const highlightKeyword = (children: any): any => {
    if (typeof children === 'string') {
      const regex = new RegExp(`(^|[\\s(\\["])(${keyword})([\\s)\\]".,!?;:-]|$)`, 'gi');
      const parts = children.split(regex);
      if (parts.length <= 1) {
        return children;
      }

      return parts.map((part, index) => {
        if (part.trim().toLowerCase() === keyword.trim().toLowerCase()) {
          return (
            <span key={index} data-chatbeat-keyword>
              {part}
            </span>
          );
        }
        return part;
      });
    }

    if (Array.isArray(children)) {
      return children.map((child, index) => (
        <React.Fragment key={index}>{highlightKeyword(child)}</React.Fragment>
      ));
    }

    return children;
  };

  const wrapKeywordInSpan = (element: Element, keyword: string, doc: Document) => {
    const regex = new RegExp(`(^|\\s)(${keyword})(\\s|$|-|:|,|;|.)`, 'gi');

    const processNode = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';

        if (regex.test(text)) {
          const newHTML = text.replace(regex, '$1<span data-chatbeat-keyword>$2</span>$3');

          const tempDiv = doc.createElement('div');
          tempDiv.innerHTML = newHTML;

          const parent = node.parentNode;
          while (tempDiv.firstChild) {
            parent?.insertBefore(tempDiv.firstChild, node);
          }
          parent?.removeChild(node);
        }
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
        return;
      }

      Array.from(node.childNodes).forEach(processNode);
    };

    processNode(element);
  };
  const processedContent = (content: string) =>
    content.replace(
      new RegExp(`(^|\\s)(${keyword})(\\s|$|-|:|,|;|.)`, 'gi'),
      '$1<span data-chatbeat-keyword>$2</span>$3'
    );

  const modelName = `tooltip-${uuidv4()}-${monitorType}`;

  const hasPositions = positions.length > 0;

  return (
    <div className={`chat-response${!hasPositions ? ' chat-response--full-width' : ''}`}>
      <div className="chat-response__message chat-message">
        <div className="chat-message__box">
          <div className="chat-message__content">
            <div className="chat-message__header">
              <div className="chat-message__position">
                {monitorType && (
                  <div className="chat-message__type">
                    <span id={modelName} className={`${getMonitorTypeIcon(monitorType)} ico--md`} />
                    {getMonitorFullName(monitorType, created)}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="chat-message__cloud-question">{prompt}</div>
            </div>
            {htmlSupportedModels.includes(monitorType) ? (
              <div
                className="chat-message__cloud"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const doc = parser.parseFromString(htmlContent, 'text/html');
                    const body = doc.querySelector('body');
                    const mainSelector = body?.querySelector(getMainSelector(monitorType));

                    if (!body) {
                      return '';
                    }

                    const hasMainSelector = Boolean(mainSelector && mainSelector.textContent?.trim());
                    const messageSelector = hasMainSelector ? mainSelector : body;

                    if (messageSelector) {
                      const isValidUrl = (str: string): boolean => {
                        if (
                          !str ||
                          str.length < 4 ||
                          /^(button|false|true|null|undefined)$/i.test(str)
                        ) {
                          return false;
                        }

                        try {
                          const urlString =
                            str.startsWith('http://') || str.startsWith('https://')
                              ? str
                              : `https://${str}`;
                          const url = new URL(urlString);

                          return (
                            (url.protocol === 'http:' || url.protocol === 'https:') &&
                            url.hostname.includes('.')
                          );
                        } catch {
                          return false;
                        }
                      };

                      const extractUrlFromButton = (button: Element): string | null => {
                        const attributes = Array.from(button.attributes);
                        for (const attr of attributes) {
                          if (isValidUrl(attr.value)) {
                            return attr.value.startsWith('http') ? attr.value : `https://${attr.value}`;
                          }
                        }

                        const text = button.textContent?.trim() || '';

                        const urlRegex =
                          /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?/g;
                        const matches = text.match(urlRegex);

                        if (matches) {
                          for (const match of matches) {
                            if (isValidUrl(match)) {
                              return match.startsWith('http') ? match : `https://${match}`;
                            }
                          }
                        }

                        return null;
                      };

                      messageSelector.querySelectorAll('button span').forEach((span) => {
                        if (!span.textContent?.trim() || span.textContent.includes('+')) {
                          span.remove();
                        }
                      });

                      messageSelector.querySelectorAll('button').forEach((button) => {
                        const url = extractUrlFromButton(button);
                        const buttonText = button.textContent?.trim().toLowerCase() || '';
                        const containsKeyword = buttonText.includes(keyword.toLowerCase());
                        const hasAutofocus = button.hasAttribute('data-spatial-navigation-autofocus');
                        const isEmpty = !button.textContent?.trim();

                        if (url) {
                          const link = doc.createElement('a');
                          link.href = url;
                          link.textContent = button.textContent?.trim() || url;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';

                          button.parentNode?.replaceChild(link, button);
                        } else if (containsKeyword && !isEmpty && !hasAutofocus) {
                          const span = doc.createElement('span');
                          span.textContent = button.textContent?.trim() || '';

                          button.parentNode?.replaceChild(span, button);
                        } else {
                          button.remove();
                        }
                      });

                      messageSelector.querySelectorAll('span, sup, div, p ').forEach((div) => {
                        const hasUserMessage = div.getAttribute('data-content') === 'user-message'; // COPILOT
                        const hasCitationCard = div.getAttribute('data-testid') === 'citation-cards-row'; // COPILOT
                        const hasDateDivider = div.getAttribute('data-testid') === 'date-divider'; // COPILOT
                        const hasNoOpener = div.getAttribute('rel') === 'noopener'; // COPILOT
                        const hasActionButtons = div.getAttribute('class')?.includes('action-buttons'); //GROK
                        const hasImageViewer = div.getAttribute('data-testid') === 'image-viewer'; //GROK
                        const hasXId = div.getAttribute('data-xid') === 'Gd7Hsc'; //AI_MODE

                        if (
                          !div.textContent?.trim() ||
                          hasUserMessage ||
                          hasCitationCard ||
                          hasDateDivider ||
                          hasNoOpener ||
                          hasActionButtons ||
                          hasImageViewer ||
                          hasXId
                        ) {
                          div.remove();
                        }
                      });

                      const highlightedChats = messageSelector.querySelector(
                        'div[data-testid="highlighted-chats"]'
                      );

                      if (highlightedChats) {
                        highlightedChats
                          .querySelectorAll(
                            'div > div[data-tabster] > div > h1[aria-label], ' +
                              'div > div[data-tabster] > div > h2[aria-label], ' +
                              'div > div[data-tabster] > div > h3[aria-label], ' +
                              'div > div[data-tabster] > div > h4[aria-label], ' +
                              'div > div[data-tabster] > div > h5[aria-label], ' +
                              'div > div[data-tabster] > div > h6[aria-label], ' +
                              'div[data-content="ai-message"] > div > h1[aria-label], ' +
                              'div[data-content="ai-message"] > div > h2[aria-label], ' +
                              'div[data-content="ai-message"] > div > h3[aria-label], ' +
                              'div[data-content="ai-message"] > div > h4[aria-label], ' +
                              'div[data-content="ai-message"] > div > h5[aria-label], ' +
                              'div[data-content="ai-message"] > div > h6[aria-label]'
                          )
                          .forEach((header) => header.remove());
                      }

                      wrapKeywordInSpan(messageSelector, keyword, doc);
                    }

                    return DOMPurify.sanitize(messageSelector?.innerHTML || '', {
                      FORBID_TAGS: [
                        'style',
                        'script',
                        'meta',
                        'html',
                        'head',
                        'body',
                        'textarea',
                        'input',
                        'svg',
                        'dialog',
                      ],
                      FORBID_ATTR: [
                        'style',
                        'class',
                        'id',
                        'data-path-to-node',
                        'aria-expanded',
                        'tabindex',
                        'data-copy',
                        'role',
                        'data-tabster-dummy',
                      ],
                    });
                  })(),
                }}
              />
            ) : (
              <>
                <ReactMarkdown
                  className="chat-message__cloud"
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    strong: ({ children }) => <strong>{highlightKeyword(children)}</strong>,
                    em: ({ children }) => <em>{highlightKeyword(children)}</em>,
                    p: ({ children }) => <p>{highlightKeyword(children)}</p>,
                    span: ({ children }) => <span>{highlightKeyword(children)}</span>,
                    li: ({ children }) => <li>{highlightKeyword(children)}</li>,
                  }}
                >
                  {organicContent ? processedContent(organicContent) : processedContent(content)}
                </ReactMarkdown>
                {organicContent && organicContent.trim() !== '' && (
                  <>
                    <div>
                      <div className="chat-message__cloud-question chat-message__cloud-question--info">
                        {t('expandOrganicAnswer')}
                        <CustomTooltip tooltipText="expandedInfo" addMargin={false} />
                      </div>
                    </div>
                    <ReactMarkdown
                      className="chat-message__cloud"
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        strong: ({ children }) => <strong>{highlightKeyword(children)}</strong>,
                        em: ({ children }) => <em>{highlightKeyword(children)}</em>,
                        p: ({ children }) => <p>{highlightKeyword(children)}</p>,
                        span: ({ children }) => <span>{highlightKeyword(children)}</span>,
                        li: ({ children }) => <li>{highlightKeyword(children)}</li>,
                      }}
                    >
                      {processedContent(content)}
                    </ReactMarkdown>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {hasPositions && (
        <div className="chat-response__stats">
          {hasPositions && (
            <div className="chat-info-box">
              <div className="chat-info-box__header">{t('positions')}</div>
              <div className="chat-info-box__content-box">
                {positions.map(({ item, position }) => (
                  <div
                    className={`chat-info-box-item ${
                      position === currentPosition ? 'chat-info-box-item--selected' : ''
                    }`}
                    key={`${item}-${position}`}
                  >
                    <div className="chat-info-box-item__position">{position}</div>
                    {item}
                  </div>
                ))}
                {organicContent && (
                  <div className="chat-info-box__info">
                    <span className="ico ico--info ico--xxs"></span>
                    <div>{t('expandedInfo')}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/*{sources.length > 0 && (*/}
          {/*  <div className="chat-info-box">*/}
          {/*    <div className="chat-info-box__header">{t('sources')}</div>*/}
          {/*    <div className="chat-info-box__source-box">*/}
          {/*      {sources.map((source) => (*/}
          {/*        <a href={source} className="source-box">*/}
          {/*          <img src={`https://favicon.is/${source}`} className="source-box__ico" />*/}
          {/*          <div>Test</div>*/}
          {/*        </a>*/}
          {/*      ))}*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*)}*/}
        </div>
      )}
    </div>
  );
};

export default ChatResponse;
